// stores/auth.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import router from '../router'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const token = ref(localStorage.getItem('token') || null)
  const userSource = ref(localStorage.getItem('user_source') || 'all')
  const userLocation = ref(null)
  const userSourceName = ref('')
  const sources = ref([])

  const API_URL = import.meta.env.VITE_API_URL || 'http://31.97.58.184:4002'

  const isAuthenticated = computed(() => !!token.value && !!user.value)

  // ========== TOKEN EXPIRY HELPER ==========
  const getTokenExpiry = () => {
    const currentToken = token.value || localStorage.getItem('token')
    if (!currentToken) return null
    
    try {
      const parts = currentToken.split('.')
      if (parts.length !== 3) return null
      
      const payloadBase64 = parts[1]
      const payload = JSON.parse(atob(payloadBase64))
      return payload.exp ? payload.exp * 1000 : null
    } catch (error) {
      console.error('Failed to decode token:', error)
      return null
    }
  }

  const isTokenExpired = () => {
    const expiry = getTokenExpiry()
    if (!expiry) return true
    return Date.now() >= expiry
  }

  const checkTokenAndLogout = () => {
    if (token.value && isTokenExpired()) {
      console.log('⏰ Token expired, logging out...')
      logout()
      
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        if (window.showNotification) {
          window.showNotification('Your session has expired. Please login again.', 'error')
        } else {
          alert('Your session has expired. Please login again.')
        }
        router.push('/login')
      }
      return true
    }
    return false
  }

  // Inisialisasi dari localStorage
  const initFromStorage = () => {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')
    const storedSource = localStorage.getItem('user_source')
    const storedLocation = localStorage.getItem('user_location')
    const storedSourceName = localStorage.getItem('user_source_name')
    
    if (storedToken && storedUser) {
      token.value = storedToken
      user.value = JSON.parse(storedUser)
      userSource.value = storedSource || 'all'
      userLocation.value = storedLocation ? JSON.parse(storedLocation) : null
      userSourceName.value = storedSourceName || ''
      return true
    }
    return false
  }

  // Fetch sources dari backend
  const fetchSources = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sources`, {
        headers: {
          'Authorization': `Bearer ${token.value}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        sources.value = data.sources || []
        return sources.value
      }
    } catch (error) {
      console.error('Error fetching sources:', error)
    }
    return []
  }

  const login = async (username, password) => {
    try {
      console.log('Attempting login to:', `${API_URL}/api/login`)
      
      const response = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ username, password })
      })
      
      const data = await response.json()
      
      if (response.ok && data.token) {
        user.value = data.user
        token.value = data.token
        userSource.value = data.user.source || 'all'
        userLocation.value = data.user.location || null
        userSourceName.value = data.user.sourceName || data.user.source || username
        
        localStorage.setItem('token', data.token)
        localStorage.setItem('user_source', userSource.value)
        localStorage.setItem('user_location', JSON.stringify(userLocation.value))
        localStorage.setItem('user_source_name', userSourceName.value)
        localStorage.setItem('user', JSON.stringify(data.user))
        
        await fetchSources()
        
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('auth-state-changed', { 
            detail: { isAuthenticated: true, user: data.user, source: userSource.value }
          }))
        }
        
        return { 
          success: true, 
          location: userLocation.value, 
          source: userSource.value 
        }
      }
      
      return { success: false, message: data.message || 'Login failed' }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, message: error.message || 'Network error' }
    }
  }

  const logout = () => {
    user.value = null
    token.value = null
    userSource.value = 'all'
    userLocation.value = null
    userSourceName.value = ''
    sources.value = []
    localStorage.removeItem('token')
    localStorage.removeItem('user_source')
    localStorage.removeItem('user_location')
    localStorage.removeItem('user_source_name')
    localStorage.removeItem('user')
    
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth-state-changed', { 
        detail: { isAuthenticated: false }
      }))
    }
  }

  const checkAuth = () => {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')
    
    if (storedToken && storedUser) {
      token.value = storedToken
      user.value = JSON.parse(storedUser)
      userSource.value = localStorage.getItem('user_source') || 'all'
      userLocation.value = JSON.parse(localStorage.getItem('user_location') || 'null')
      userSourceName.value = localStorage.getItem('user_source_name') || ''
      return true
    }
    return false
  }

  const getMapCenter = () => {
    if (userLocation.value && userLocation.value.lat && userLocation.value.lng) {
      return {
        lat: userLocation.value.lat,
        lng: userLocation.value.lng,
        zoom: 14
      }
    }
    return { lat: -2.5489, lng: 118.0149, zoom: 5 }
  }

  return {
    user,
    token,
    userSource,
    userLocation,
    userSourceName,
    sources,
    isAuthenticated,
    initFromStorage,
    fetchSources,
    login,
    logout,
    checkAuth,
    getMapCenter,
    getTokenExpiry,
    isTokenExpired,
    checkTokenAndLogout
  }
})